import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { API_BASE, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAdaContext } from "@/context/AdaContext";
import { toast } from "@/components/Toaster";
import Confirm from "@/components/Confirm";
import {
  Sparkles, Send, Plus, MessageSquare, FileText, RefreshCw, Loader2,
  ShieldCheck, ShieldOff, ShieldAlert, Trash2, X, ChevronRight, Info,
  MoreVertical, Minus, Eye, EyeOff, FolderLock, ArrowLeft,
  Maximize2, Minimize2, AlertTriangle, CheckCircle2,
  ThumbsUp, ThumbsDown, Copy, Check,
} from "lucide-react";

/**
 * Ada floating chat widget.
 *
 * A ~56 px sparkles bubble anchored to the bottom-right of the viewport.
 * Slides in with a 500 ms delay after the DataRoom mounts. Clicking the
 * bubble expands a chat window (400 × 640) above it — familiar
 * Messenger / Intercom pattern, purpose-built for diligence.
 *
 * The window has four sub-views:
 *   • "chat"     — active conversation (default landing)
 *   • "history"  — list of prior conversations
 *   • "access"   — "What Ada can see for me" transparency drawer
 *   • "settings" — Owner-only Enable/Disable + Admin Reindex
 *
 * Streaming, follow-ups, citations, and permission-scoped retrieval are
 * unchanged — this is a UX shell around the same backend contract.
 */
export default function AdaBubble({ roomId, room, isAdmin }) {
  const { user } = useAuth();
  // Ada is gated to Owners + Admins only. Owners have role="admin"
  // in the DB (plus an `owner` flag), so a single role check covers
  // both. Non-admin room members get zero visual footprint — no
  // pill, no window, no chip.
  const adaAllowed = user?.role === "admin";
  // Persistent open/closed state + active focus (folder / document)
  // published by the current page. Living in a context lets Ada
  // survive route transitions (DataRoom → DocumentViewer).
  const {
    isOpen: ctxIsOpen, open: ctxOpen, close: ctxClose,
    activeContext,
  } = useAdaContext();

  // ---- Config gate ----------------------------------------------------
  // `enabled` false OR key missing → bubble hides entirely for
  // non-owners. Owners always see the bubble so they can re-enable.
  const [cfg, setCfg] = useState(null);
  const [visible, setVisible] = useState(false);        // controls slide-in mount
  const [pulseKey, setPulseKey] = useState(0);          // bumping this restarts the 3s pulse
  const [pulsing, setPulsing] = useState(false);        // capped at ~3 rings
  // Bind window "open" to the shared context so navigating between
  // DataRoom and DocumentViewer preserves whether Ada is expanded.
  const open = ctxIsOpen;
  const [view, setView] = useState("chat");             // 'chat' | 'history' | 'access' | 'settings'
  const [streaming, setStreaming] = useState(false);    // Ada is generating a reply
  const [answerReady, setAnswerReady] = useState(false);// stream finished while minimised → show toast
  const [everOpened, setEverOpened] = useState(false);
  const prevStreamingRef = useRef(false);
  const answerToastTimerRef = useRef(null);

  // Restart the 3 s pulse burst whenever `pulseKey` changes — used for
  // the initial slide-in AND every time the user minimises the window.
  useEffect(() => {
    if (pulseKey === 0) return undefined;
    setPulsing(true);
    const t = setTimeout(() => setPulsing(false), 3000);
    return () => clearTimeout(t);
  }, [pulseKey]);

  useEffect(() => {
    if (!adaAllowed) return undefined;           // skip API call for non-admins
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/rooms/${roomId}/ada/config`);
        if (!alive) return;
        setCfg(data);
      } catch { /* ignore — bubble stays hidden */ }
    })();
    // 500 ms slide-in delay + initial 3 s pulse burst.
    const showT = setTimeout(() => {
      setVisible(true);
      setPulseKey((k) => k + 1);
    }, 500);
    return () => { alive = false; clearTimeout(showT); };
  }, [roomId, adaAllowed]);

  // Detect: a streaming reply just finished AND the window is closed →
  // show a "Ada answered" toast beside the bubble for 4 s.
  useEffect(() => {
    const wasStreaming = prevStreamingRef.current;
    prevStreamingRef.current = streaming;
    if (wasStreaming && !streaming && !open) {
      setAnswerReady(true);
      clearTimeout(answerToastTimerRef.current);
      answerToastTimerRef.current = setTimeout(() => setAnswerReady(false), 4000);
    }
    return () => { /* handled inline */ };
  }, [streaming, open]);
  useEffect(() => () => clearTimeout(answerToastTimerRef.current), []);

  const shouldRender = useMemo(() => {
    if (!adaAllowed) return false;                     // Owners/Admins only
    if (!cfg) return false;
    if (!cfg.key_configured) return false;
    if (cfg.enabled) return true;
    return !!cfg.can_edit;                             // owner sees bubble even when disabled
  }, [adaAllowed, cfg]);

  if (!shouldRender) return null;

  const openWindow = () => {
    ctxOpen();
    setEverOpened(true);
    setView("chat");
    setPulsing(false);
    setAnswerReady(false);
    clearTimeout(answerToastTimerRef.current);
  };
  const closeWindow = () => {
    ctxClose();
    // Every minimise re-triggers the 3 s pulse to remind the user Ada
    // is still there — same feel as when she first slides in.
    setPulseKey((k) => k + 1);
  };

  // Breathing glow only while a reply is streaming AND the bubble is
  // the sole visible surface (window minimised).
  const showBreathing = streaming && !open;

  return (
    <>
      {/* Anchor wraps the bubble + pulse rings so they can extend
          BEYOND the avatar's rounded clip. `overflow-visible` is critical
          here — the previous version had the ring inside the button with
          `overflow-hidden` and it was being clipped away. */}
      <div
        className={`fixed z-40 bottom-8 right-8 w-16 h-16 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
          ${open ? "scale-0 opacity-0 pointer-events-none" : ""}
          ${visible ? "translate-x-0 opacity-100" : "translate-x-24 opacity-0"}`}
      >
        {/* Pulse rings — two concentric, staggered for depth */}
        {pulsing && (
          <>
            <span
              key={`p1-${pulseKey}`}
              className="absolute inset-0 rounded-full ring-2 ring-primary/60 ada-ping-1 pointer-events-none"
              aria-hidden
            />
            <span
              key={`p2-${pulseKey}`}
              className="absolute inset-0 rounded-full ring-2 ring-primary/40 ada-ping-2 pointer-events-none"
              aria-hidden
            />
          </>
        )}

        {/* The clickable bubble itself */}
        <button
          data-testid="ada-bubble"
          onClick={openWindow}
          aria-label="Open Ada — diligence copilot"
          className={`relative w-16 h-16 rounded-full overflow-hidden
            shadow-[0_10px_30px_-6px_rgba(16,185,129,0.55)] hover:shadow-[0_18px_40px_-6px_rgba(16,185,129,0.75)]
            ring-1 ring-primary/40 hover:ring-primary
            transition-transform duration-300
            hover:scale-110 active:scale-95
            ${showBreathing ? "ada-breathing" : ""}`}
        >
          <img
            src="/ada/ada-avatar.png"
            alt="Ada"
            className="w-full h-full object-cover"
            draggable={false}
          />
        </button>

        {/* "Ada answered — click to view" toast */}
        {answerReady && (
          <button
            data-testid="ada-answer-toast"
            onClick={openWindow}
            className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap
              px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-xs
              shadow-lg shadow-primary/40 flex items-center gap-2
              ada-toast-slide-in"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Ada answered — click to view</span>
            <X
              className="w-3 h-3 opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                setAnswerReady(false);
                clearTimeout(answerToastTimerRef.current);
              }}
            />
          </button>
        )}
      </div>

      {/* Chat window — mounted whenever it's been opened OR the
          persistent open-state is already true (route transition
          from a page where Ada was open). Keeps a stream-in-progress
          alive across minimise/expand. */}
      {(everOpened || open) && (
        <AdaChatWindow
          roomId={roomId}
          room={room}
          user={user}
          isAdmin={isAdmin}
          cfg={cfg}
          setCfg={setCfg}
          view={view}
          setView={setView}
          onClose={closeWindow}
          onStreamingChange={setStreaming}
          visible={visible}
          hidden={!open}
          activeContext={activeContext}
        />
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// The chat window (expanded state)
// ---------------------------------------------------------------------------

function AdaChatWindow({ roomId, room, user, isAdmin, cfg, setCfg, view, setView, onClose, onStreamingChange, visible, hidden, activeContext }) {
  // ---- Conversation state --------------------------------------------
  const windowRef = useRef(null);

  // Window dimensions — persisted per browser so the user's preferred
  // size sticks across page loads. Bounds keep the panel from becoming
  // either uselessly small or hilariously oversized.
  const MIN_W = 380, MIN_H = 480;
  const MAX_W = 720, MAX_H = 900;
  // Storage key is versioned so shipping a wider default reaches
  // existing users. Bump the suffix on future default-shape changes.
  const DIMS_STORAGE_KEY = "ada.windowDims.v2";
  const [dims, setDims] = useState(() => {
    try {
      const raw = localStorage.getItem(DIMS_STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p?.w === "number" && typeof p?.h === "number") return p;
      }
    } catch { /* ignore corrupt storage */ }
    return { w: 560, h: 720 };
  });
  const [maximised, setMaximised] = useState(false);
  const preMaxDimsRef = useRef(null);

  const toggleMaximise = () => {
    if (maximised) {
      // Restore
      if (preMaxDimsRef.current) setDims(preMaxDimsRef.current);
      setMaximised(false);
    } else {
      preMaxDimsRef.current = dims;
      const w = Math.min(MAX_W, Math.round(window.innerWidth * 0.8));
      const h = Math.min(MAX_H, Math.round(window.innerHeight * 0.8));
      setDims({ w, h });
      setMaximised(true);
    }
  };
  useEffect(() => {
    try { localStorage.setItem(DIMS_STORAGE_KEY, JSON.stringify(dims)); }
    catch { /* quota — non-fatal */ }
  }, [dims]);

  // Generic edge / corner drag handler. Anchored at bottom-right so the
  // top edge drags height, the left edge drags width, and the top-left
  // corner drags both.
  const startDrag = (dir) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX, startY = e.clientY;
    const startW = dims.w, startH = dims.h;
    const move = (ev) => {
      let nw = startW, nh = startH;
      if (dir.includes("w")) nw = startW - (ev.clientX - startX);
      if (dir.includes("n")) nh = startH - (ev.clientY - startY);
      const maxW = Math.min(MAX_W, window.innerWidth - 48);
      const maxH = Math.min(MAX_H, window.innerHeight - 80);
      setDims({
        w: Math.min(maxW, Math.max(MIN_W, nw)),
        h: Math.min(maxH, Math.max(MIN_H, nh)),
      });
      // Manual drag → exit maximised mode so the icon reflects reality.
      if (maximised) setMaximised(false);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = dir === "nw" ? "nwse-resize" : dir === "w" ? "ew-resize" : "ns-resize";
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [status, setStatus] = useState(null);
  const [access, setAccess] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  // Bottom indexing banner: user can dismiss it. Resets whenever we
  // switch rooms so a fresh room re-surfaces the notice.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  useEffect(() => { setBannerDismissed(false); }, [roomId]);

  // Click-outside → minimise. Restored per user preference — Ada
  // shouldn't sit persistently on the screen; a click anywhere else
  // tucks her back into the pill. The persistence-across-routes
  // behaviour still lives in the AdaContext (so opening a document
  // keeps her open until you click away), but on a single page a
  // click on the folder tree / a document card auto-minimises.
  //
  // Skipped when the window is currently hidden (nothing to close) or
  // a Confirm modal is open (its portal renders outside the window
  // ref so a click on it would spuriously close Ada).
  useEffect(() => {
    if (confirmState || hidden) return undefined;
    const onDoc = (e) => {
      if (!windowRef.current) return;
      // Ignore clicks on the bubble itself (it does its own open/close).
      const bubble = document.querySelector('[data-testid="ada-bubble"]');
      if (bubble && bubble.contains(e.target)) return;
      // Ignore clicks inside any popover / dialog anchored to <body> —
      // shadcn portals + our Confirm modal live outside windowRef.
      if (e.target.closest('[role="dialog"], .toaster, [data-radix-popper-content-wrapper]')) return;
      if (!windowRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose, confirmState, hidden]);

  // Auto-dock: shrink to a compact panel when the user is viewing a
  // document so Ada doesn't cover the reader. Remembers the previous
  // (user-preferred) size and restores it when the user navigates
  // away from the document viewer.
  const preDockDimsRef = useRef(null);
  const wasDockedRef = useRef(false);
  const isViewingDoc = activeContext?.type === "document";
  useEffect(() => {
    if (isViewingDoc && !wasDockedRef.current) {
      preDockDimsRef.current = dims;
      wasDockedRef.current = true;
      setDims({ w: 400, h: 560 });
    } else if (!isViewingDoc && wasDockedRef.current) {
      if (preDockDimsRef.current) setDims(preDockDimsRef.current);
      wasDockedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingDoc]);

  // ---- Compose / streaming -------------------------------------------
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [streamCitations, setStreamCitations] = useState([]);
  const [streamFollowups, setStreamFollowups] = useState([]);
  const [streamVerification, setStreamVerification] = useState(null);
  const [streamStage, setStreamStage] = useState(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  // Smooth "typing" buffer — raw tokens land in `rawStreamRef`; we
  // drip characters into `streamText` via a rAF loop at a steady rate
  // so the answer reads like a real analyst typing instead of arriving
  // in jerky provider-buffered bursts. Rate adapts to backlog so we
  // never fall too far behind the model.
  const rawStreamRef = useRef("");
  const displayedRef = useRef("");
  const dripActiveRef = useRef(false);
  const dripDoneRef = useRef(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get(`/rooms/${roomId}/ada/status`);
      setStatus(data);
    } catch { /* silent */ }
  };
  const loadConvs = async () => {
    try {
      const { data } = await api.get(`/rooms/${roomId}/ada/conversations`);
      setConvs(data || []);
      if ((data || []).length && !activeId) setActiveId(data[0].id);
    } catch { /* silent */ }
  };
  const loadMessages = async (cid) => {
    if (!cid) { setMessages([]); return; }
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/rooms/${roomId}/ada/conversations/${cid}/messages`);
      setMessages(data || []);
    } catch (e) {
      toast.error(formatError(e));
      setMessages([]);
    } finally { setLoadingMsgs(false); }
  };
  const loadAccess = async () => {
    try {
      const { data } = await api.get(`/rooms/${roomId}/ada/access`);
      setAccess(data);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { loadStatus(); loadConvs(); /* eslint-disable-next-line */ }, [roomId]);
  useEffect(() => { loadMessages(activeId); /* eslint-disable-next-line */ }, [activeId]);
  // While indexing is in progress we poll the status endpoint so the
  // banner + coverage pill update as chunks are embedded. Poll stops
  // once every doc is indexed (or on unmount) — no idle traffic.
  useEffect(() => {
    if (!status) return undefined;
    const pending = (status.total_docs || 0) - (status.indexed_docs || 0);
    if (pending <= 0) return undefined;
    const iv = setInterval(() => { loadStatus(); }, 4000);
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [status?.total_docs, status?.indexed_docs, roomId]);
  // Lift streaming state to the parent so the (minimised) bubble can
  // show a breathing glow while Ada is generating a reply.
  useEffect(() => { onStreamingChange?.(sending); }, [sending, onStreamingChange]);
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamText, view]);

  // ---- Owner toggle ---------------------------------------------------
  const toggleEnabled = async () => {
    if (!cfg?.can_edit) return;
    setSavingCfg(true);
    try {
      const { data } = await api.patch(`/rooms/${roomId}/ada/config`, {
        enabled: !cfg.enabled,
      });
      setCfg({ ...cfg, ...data });
      toast.success(data.enabled ? "Ada enabled" : "Ada disabled");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSavingCfg(false); }
  };

  const reindex = async () => {
    if (reindexing) return;
    setReindexing(true);
    try {
      await api.post(`/rooms/${roomId}/ada/reindex`, { force: false });
      toast.success("Indexing started");
      let ticks = 0;
      const tick = async () => {
        ticks += 1;
        await loadStatus();
        if (ticks < 20) setTimeout(tick, 4000);
        else setReindexing(false);
      };
      tick();
    } catch (e) { toast.error(formatError(e)); setReindexing(false); }
  };

  // ---- Conversation lifecycle ----------------------------------------
  const newConversation = async () => {
    try {
      const { data } = await api.post(`/rooms/${roomId}/ada/conversations`);
      setConvs([data, ...convs]);
      setActiveId(data.id);
      setMessages([]);
      setInput("");
      setStreamText("");
      setStreamCitations([]);
      setStreamFollowups([]);
      setView("chat");
    } catch (e) { toast.error(formatError(e)); }
  };
  const deleteConversation = (c) => {
    setConfirmState({
      title: "Delete conversation?",
      message: `"${c.title || "New chat"}" and its messages will be removed.`,
      confirmLabel: "Delete",
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/rooms/${roomId}/ada/conversations/${c.id}`);
          const next = convs.filter((x) => x.id !== c.id);
          setConvs(next);
          if (activeId === c.id) {
            setActiveId(next[0]?.id || null);
            setMessages([]);
          }
        } catch (e) { toast.error(formatError(e)); throw e; }
      },
    });
  };

  // ---- Streaming send -------------------------------------------------
  const send = async () => {
    const question = input.trim();
    if (!question || sending) return;
    if (!cfg?.enabled) { toast.error("Ada is disabled"); return; }
    setSending(true);
    setInput("");
    setStreamText("");
    setStreamCitations([]);
    setStreamFollowups([]);
    setStreamVerification(null);
    setStreamStage("retrieving");
    rawStreamRef.current = "";
    displayedRef.current = "";
    dripDoneRef.current = false;
    setMessages((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: "user", content: question, created_at: new Date().toISOString(), citations: [] },
    ]);

    // Smooth character-drip loop — runs while the stream is arriving
    // (and briefly after) to release buffered characters at a steady
    // pace. Base rate ≈ 55 chars/sec; if the backlog is large we
    // accelerate up to 180 chars/sec so we never fall behind the model.
    const startDripLoop = () => {
      if (dripActiveRef.current) return;
      dripActiveRef.current = true;
      let lastTick = performance.now();
      const step = (now) => {
        const dt = Math.max(0, now - lastTick);
        lastTick = now;
        const backlog = rawStreamRef.current.length - displayedRef.current.length;
        // Adaptive rate: 55 chars/s baseline, +1 char/sec per char of
        // backlog beyond 40, capped at 180.
        const rate = Math.min(180, 55 + Math.max(0, backlog - 40));
        const toEmit = Math.max(1, Math.round((rate * dt) / 1000));
        if (backlog > 0) {
          const next = displayedRef.current.length + Math.min(backlog, toEmit);
          displayedRef.current = rawStreamRef.current.slice(0, next);
          setStreamText(displayedRef.current);
        } else if (dripDoneRef.current) {
          dripActiveRef.current = false;
          return;
        }
        if (dripActiveRef.current) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`${API_BASE}/rooms/${roomId}/ada/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation_id: activeId,
          message: question,
          // UI focus context (folder / document / page) so Ada can
          // interpret "this", "here", "it" and bias retrieval to the
          // doc the user is currently reading.
          context: activeContext || undefined,
        }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      startDripLoop();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantCitations = [];
      let assistantFollowups = [];
      let assistantVerification = null;
      let convIdFromServer = activeId;
      let asstMessageId = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          let event = "message"; let data = "";
          for (const l of lines) {
            if (l.startsWith("event:")) event = l.slice(6).trim();
            else if (l.startsWith("data:")) data += l.slice(5).trim();
          }
          if (!data) continue;
          let payload;
          try { payload = JSON.parse(data); } catch { continue; }
          if (event === "token") {
            // Feed raw token into the drip buffer — displayed text is
            // driven by the rAF loop, not the network cadence.
            rawStreamRef.current += payload.text || "";
          }
          else if (event === "status") { setStreamStage(payload.stage || null); }
          else if (event === "citations") { assistantCitations = payload.citations || []; setStreamCitations(assistantCitations); }
          else if (event === "followups") { assistantFollowups = payload.followups || []; setStreamFollowups(assistantFollowups); }
          else if (event === "verification") { assistantVerification = payload || null; setStreamVerification(assistantVerification); }
          else if (event === "done") { convIdFromServer = payload.conversation_id || activeId; asstMessageId = payload.message_id; }
          else if (event === "error") { toast.error(payload.detail || "Ada failed"); }
        }
      }
      // Signal the drip loop that no more tokens are coming; wait for
      // it to catch up so the assistant message we persist matches
      // what the user actually saw.
      dripDoneRef.current = true;
      const finalText = rawStreamRef.current;
      // Give the drip a max 1.5 s to catch up; then hard-flush.
      const flushDeadline = performance.now() + 1500;
      while (displayedRef.current.length < finalText.length && performance.now() < flushDeadline) {
        await new Promise((r) => setTimeout(r, 30));
      }
      if (displayedRef.current !== finalText) {
        displayedRef.current = finalText;
        setStreamText(finalText);
      }
      setMessages((prev) => [
        ...prev,
        {
          id: asstMessageId || `local-${Date.now()}`,
          role: "assistant",
          content: finalText,
          citations: assistantCitations,
          followups: assistantFollowups,
          verification: assistantVerification || {},
          created_at: new Date().toISOString(),
        },
      ]);
      setStreamText(""); setStreamCitations([]); setStreamFollowups([]); setStreamVerification(null); setStreamStage(null);
      if (convIdFromServer && convIdFromServer !== activeId) {
        setActiveId(convIdFromServer);
        loadConvs();
      } else {
        setConvs((prev) => {
          const idx = prev.findIndex((c) => c.id === convIdFromServer);
          if (idx < 0) return prev;
          const next = [...prev];
          const c = { ...next[idx], updated_at: new Date().toISOString(),
                       title: next[idx].title === "New chat" ? question.slice(0, 60) : next[idx].title };
          next.splice(idx, 1);
          return [c, ...next];
        });
      }
      // Refresh status pill count after each answer (index may have grown)
      loadStatus();
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Ada stream error");
    } finally {
      dripDoneRef.current = true;
      dripActiveRef.current = false;
      setSending(false);
      setStreamStage(null);
      abortRef.current = null;
    }
  };

  const stopStream = () => abortRef.current?.abort();
  const activeConv = useMemo(() => convs.find((c) => c.id === activeId) || null, [convs, activeId]);
  const suggestions = [
    "Introduce yourself and tell me what you can help with.",
    "What is the company's ACN and jurisdiction of registration?",
    "Summarise the key terms of the Series A Term Sheet.",
    "List every director appointment resolution in this room.",
  ];

  // ---------------------------------------------------------------------
  // Header sub-views
  // ---------------------------------------------------------------------
  const isDisabled = !cfg?.enabled;

  return (
    <div
      ref={windowRef}
      data-testid="ada-window"
      className={`fixed z-40 bottom-8 right-8
        rounded-2xl bg-card border border-border shadow-2xl overflow-hidden flex flex-col
        transition-[opacity,transform] duration-300 ease-out
        ${hidden ? "pointer-events-none scale-95 opacity-0 translate-y-2" : (visible ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-2")}`}
      style={{
        transformOrigin: "bottom right",
        width: `min(${dims.w}px, calc(100vw - 2.5rem))`,
        height: `min(${dims.h}px, calc(100vh - 4rem))`,
      }}
      aria-hidden={hidden}
    >
      {/* Resize handles — invisible drag-catchers on the top edge, the
          left edge, and a slightly larger corner grip for both axes.
          A visible pill grip sits mid-height on the left edge so users
          immediately see the panel is resizable. */}
      {!hidden && (
        <>
          <div
            onMouseDown={startDrag("n")}
            className="absolute top-0 left-3 right-3 h-1.5 cursor-ns-resize z-10"
            aria-label="Resize height"
          />
          <div
            onMouseDown={startDrag("w")}
            className="absolute left-0 top-3 bottom-3 w-1.5 cursor-ew-resize z-10 group/wedge"
            aria-label="Resize width"
          >
            {/* Persistent visible affordance — a slim pill hint mid-height */}
            <span
              className="absolute left-[3px] top-1/2 -translate-y-1/2 w-1 h-12 rounded-full
                bg-muted-foreground/25 group-hover/wedge:bg-primary/70 transition-colors pointer-events-none"
            />
          </div>
          <div
            onMouseDown={startDrag("nw")}
            className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize z-10 group/corner"
            aria-label="Resize"
          >
            <span className="absolute inset-1 rounded-tl-md pointer-events-none
              opacity-0 group-hover/corner:opacity-70 transition"
              style={{
                background: "linear-gradient(135deg, hsl(var(--muted-foreground)) 0 1px, transparent 1px 3px, hsl(var(--muted-foreground)) 3px 4px, transparent 4px 6px, hsl(var(--muted-foreground)) 6px 7px, transparent 7px)",
              }}
            />
          </div>
        </>
      )}
      {/* Header */}
      <ChatHeader
        view={view} setView={setView}
        activeConv={activeConv} status={status} cfg={cfg}
        isAdmin={isAdmin}
        maximised={maximised}
        onToggleMaximise={toggleMaximise}
        onNewChat={newConversation}
        onOpenAccess={() => { setView("access"); if (!access) loadAccess(); }}
        onClose={onClose}
      />

      {/* Body — swaps between chat / history / access / settings */}
      {isDisabled && !view.startsWith("settings") ? (
        <DisabledEmpty canEdit={cfg?.can_edit} savingCfg={savingCfg} onToggle={toggleEnabled} />
      ) : view === "chat" ? (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ada-messages">
            {loadingMsgs ? (
              <Skeleton />
            ) : messages.length === 0 && !streamText ? (
              <EmptyState suggestions={suggestions} onPick={(s) => setInput(s)} />
            ) : (
              <>
                {messages.map((m) => (
                  <Bubble key={m.id} msg={m} roomId={roomId}
                    onFollowup={(q) => setInput(q)}
                    onClose={onClose} />
                ))}
                {sending && (streamText || streamCitations.length > 0) && (
                  <Bubble live msg={{ role: "assistant", content: streamText || "…", citations: streamCitations, followups: streamFollowups, verification: streamVerification || {} }} roomId={roomId} onClose={onClose} />
                )}
                {sending && !streamText && (
                  <div className="text-xs text-muted-foreground flex items-center gap-2 pl-1" data-testid="ada-stage-indicator">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {streamStage === "retrieving"
                      ? "Searching your documents…"
                      : streamStage === "thinking"
                      ? "Ada is composing the answer…"
                      : "Ada is thinking…"}
                  </div>
                )}
              </>
            )}
          </div>
          {status && (status.total_docs || 0) > (status.indexed_docs || 0) && !bannerDismissed && (
            <div
              data-testid="ada-indexing-banner"
              className="mx-4 mb-2 rounded-md bg-amber-500/8 border border-amber-500/25 px-3 py-2 flex items-start gap-2"
            >
              <span className="relative inline-flex w-1.5 h-1.5 mt-1 shrink-0">
                <span className="absolute inline-flex w-full h-full rounded-full bg-amber-500 opacity-75 animate-ping"></span>
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[11.5px] text-amber-700 leading-snug">
                  <strong>Indexing in progress</strong> — {status.indexed_docs} of {status.total_docs} documents ready. Ada can already answer from what&rsquo;s indexed; answers may become more complete as the remaining {status.total_docs - status.indexed_docs} file{(status.total_docs - status.indexed_docs) === 1 ? "" : "s"} finish embedding.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setBannerDismissed(true)}
                data-testid="ada-indexing-banner-dismiss"
                aria-label="Dismiss indexing notice"
                className="shrink-0 -mr-1 -mt-0.5 p-1 rounded-md text-amber-700/70 hover:text-amber-800 hover:bg-amber-500/10 transition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {/* Active-context chip — shown when the user is viewing a
              folder or document. Tells Ada what "this" refers to. */}
          {activeContext && activeContext.type && (
            <div
              data-testid="ada-context-chip"
              className="mx-4 mb-2 flex items-center gap-2 rounded-md bg-primary/8 border border-primary/25 px-2.5 py-1.5 text-[11px]"
              title="Ada will interpret 'this', 'here', 'it' as this location."
            >
              {activeContext.type === "document" ? (
                <FileText className="w-3 h-3 shrink-0 text-primary" />
              ) : (
                <FolderLock className="w-3 h-3 shrink-0 text-primary" />
              )}
              <span className="text-primary/90 shrink-0">In view:</span>
              <span className="truncate text-foreground" title={activeContext.name || ""}>
                {activeContext.name || (activeContext.type === "document" ? "Document" : "Folder")}
                {activeContext.type === "document" && activeContext.page ? ` · p${activeContext.page}` : ""}
              </span>
            </div>
          )}
          <Composer input={input} setInput={setInput} sending={sending} onSend={send} onStop={stopStream} />
        </>
      ) : view === "history" ? (
        <HistoryView
          convs={convs} activeId={activeId}
          onOpen={(id) => { setActiveId(id); setView("chat"); }}
          onDelete={deleteConversation}
          onNewChat={newConversation}
        />
      ) : view === "access" ? (
        <AccessView access={access} status={status} onBack={() => setView("chat")} />
      ) : view === "settings" ? (
        <SettingsView
          cfg={cfg} status={status}
          isAdmin={isAdmin} savingCfg={savingCfg}
          reindexing={reindexing}
          onToggle={toggleEnabled}
          onReindex={reindex}
          onBack={() => setView("chat")}
        />
      ) : null}

      <Confirm state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function ChatHeader({ view, setView, activeConv, status, cfg, isAdmin, maximised, onToggleMaximise, onNewChat, onOpenAccess, onClose }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const title = view === "history" ? "Chat history"
             : view === "access" ? "What Ada can see for you"
             : view === "settings" ? "Ada settings"
             : (activeConv?.title || "Ada");

  const showBack = view !== "chat";

  return (
    <div className="shrink-0 px-3.5 py-3 border-b border-border bg-gradient-to-br from-primary/5 to-transparent flex items-center gap-2">
      {showBack ? (
        <button
          onClick={() => setView("chat")}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition"
          aria-label="Back to chat"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      ) : (
        <div className="w-8 h-8 rounded-full overflow-hidden ring-1 ring-primary/30 shrink-0">
          <img src="/ada/ada-avatar.png" alt="Ada" className="w-full h-full object-cover" draggable={false} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{title}</div>
        {view === "chat" && status && (() => {
          const isIndexing = (status.total_docs || 0) > (status.indexed_docs || 0);
          if (isIndexing) {
            const pct = status.coverage_pct ?? 0;
            const remaining = (status.total_docs || 0) - (status.indexed_docs || 0);
            return (
              <button
                data-testid="ada-status-indexing"
                onClick={onOpenAccess}
                className="mt-1 flex flex-col gap-1 w-full text-left group"
                title={`Indexing ${remaining} more file${remaining === 1 ? "" : "s"} — Ada can already answer from what's ready.`}
              >
                <div className="flex items-center gap-1.5 mono text-[10px] text-amber-600 group-hover:text-amber-700 transition">
                  <span className="relative inline-flex w-1.5 h-1.5 shrink-0">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-amber-500 opacity-75 animate-ping"></span>
                    <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  </span>
                  <span className="tabular-nums">Indexing · {status.indexed_docs}/{status.total_docs}</span>
                  <span className="text-amber-500/70">·</span>
                  <span className="tabular-nums">{pct}%</span>
                </div>
                <div className="h-0.5 w-full rounded-full bg-amber-500/15 overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all duration-500 ease-out"
                    style={{ width: `${Math.max(4, Math.min(100, pct))}%` }}
                  />
                </div>
              </button>
            );
          }
          return (
            <button
              data-testid="ada-status-pill"
              onClick={onOpenAccess}
              className="mt-0.5 inline-flex items-center gap-1.5 mono text-[10px] text-emerald-600 hover:text-emerald-700 transition"
              title={isAdmin
                ? `${status.indexed_docs}/${status.total_docs} docs indexed — click to view access`
                : "Click to see what Ada can access for you"}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="truncate">Grounded in your accessible documents</span>
            </button>
          );
        })()}
        {view === "chat" && !status && (
          <div className="mono text-[10px] text-muted-foreground truncate mt-0.5">
            Grounded in your accessible documents
          </div>
        )}
      </div>
      <div className="relative flex items-center gap-1" ref={menuRef}>
        <button
          onClick={onNewChat}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition"
          title="New chat"
          data-testid="ada-new-chat-btn"
        >
          <Plus className="w-4 h-4" />
        </button>
        <button
          onClick={onToggleMaximise}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition"
          title={maximised ? "Restore size" : "Maximise (80% of viewport)"}
          data-testid="ada-maximise-btn"
        >
          {maximised
            ? <Minimize2 className="w-3.5 h-3.5" />
            : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition"
          aria-label="More options"
          data-testid="ada-menu-btn"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition"
          aria-label="Minimise Ada"
          data-testid="ada-close-btn"
        >
          <Minus className="w-4 h-4" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-9 z-10 w-52 rounded-md bg-card border border-border shadow-xl py-1 text-sm">
            <MenuItem icon={MessageSquare} label="Chat history" onClick={() => { setView("history"); setMenuOpen(false); }} testId="ada-menu-history" />
            <MenuItem icon={Eye} label="What Ada can see for you" onClick={() => { onOpenAccess(); setMenuOpen(false); }} testId="ada-menu-access" />
            {(isAdmin || cfg?.can_edit) && (
              <>
                <div className="my-1 border-t border-border" />
                <MenuItem icon={ShieldCheck} label="Settings" onClick={() => { setView("settings"); setMenuOpen(false); }} testId="ada-menu-settings" />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary/70 text-left transition"
    >
      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      <span>{label}</span>
    </button>
  );
}


// ---------------------------------------------------------------------------
// Message bubble (with citations + follow-ups)
// ---------------------------------------------------------------------------

/**
 * Ada's answers are Markdown from the model. We render them with tight,
 * boardroom-appropriate typography — no heading levels leak in (h1/h2
 * would look like shouting inside a chat bubble), lists are compact,
 * inline `code` looks like a subtle mono tag, and citations that come
 * back as `[Doc: Name · p3]` are rendered as pill chips inline for a
 * cleaner read than raw brackets.
 */
const CITATION_INLINE = /\[Doc:\s*(.+?)\s*·\s*p(\d+)\]/g;

function renderTextWithCitationChips(children) {
  // react-markdown feeds us an array of React children per paragraph /
  // list-item. We walk strings, split on citation tokens, and swap each
  // token for a small pill. Non-string children pass through unchanged.
  const out = [];
  const arr = Array.isArray(children) ? children : [children];
  arr.forEach((child, ci) => {
    if (typeof child !== "string") {
      out.push(child);
      return;
    }
    let last = 0;
    let match;
    const re = new RegExp(CITATION_INLINE.source, "g");
    let idx = 0;
    while ((match = re.exec(child)) !== null) {
      if (match.index > last) out.push(child.slice(last, match.index));
      const name = match[1].trim();
      const page = match[2];
      out.push(
        <span
          key={`c-${ci}-${idx++}`}
          className="inline-flex items-center gap-0.5 align-baseline mx-0.5 px-1.5 py-[1px] rounded-full bg-primary/10 text-primary text-[10px] mono uppercase tracking-wider whitespace-nowrap"
          title={`${name} · p${page}`}
        >
          <span className="max-w-[110px] truncate">{name}</span>
          <span className="opacity-70">·p{page}</span>
        </span>
      );
      last = match.index + match[0].length;
    }
    if (last < child.length) out.push(child.slice(last));
  });
  return out;
}

function AdaMarkdown({ text }) {
  if (!text) return null;
  return (
    <div className="ada-md text-[13px] leading-[1.55]">
      <ReactMarkdown remarkPlugins={ADA_MD_REMARK} components={ADA_MD_COMPONENTS}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

const ADA_MD_REMARK = [remarkGfm];

// Hoisted outside render so React reconciles component identity across
// streaming updates (avoids no-unstable-nested-components lint + wasted
// remounts on every token delta).
const MdH = ({ children }) => <p className="font-semibold mt-2 mb-1">{renderTextWithCitationChips(children)}</p>;
const MdH3 = ({ children }) => <p className="font-semibold mt-1.5 mb-0.5">{renderTextWithCitationChips(children)}</p>;
const MdP = ({ children }) => <p className="mb-2 last:mb-0">{renderTextWithCitationChips(children)}</p>;
const MdStrong = ({ children }) => <strong className="font-semibold text-foreground">{renderTextWithCitationChips(children)}</strong>;
const MdEm = ({ children }) => <em className="italic">{renderTextWithCitationChips(children)}</em>;
const MdUl = ({ children }) => <ul className="list-disc pl-4 my-1.5 space-y-0.5 marker:text-muted-foreground/70">{children}</ul>;
const MdOl = ({ children }) => <ol className="list-decimal pl-4 my-1.5 space-y-0.5 marker:text-muted-foreground/70">{children}</ol>;
const MdLi = ({ children }) => <li className="pl-0.5">{renderTextWithCitationChips(children)}</li>;
const MdHr = () => <hr className="my-2 border-border/60" />;
const MdBlockquote = ({ children }) => (
  <blockquote className="border-l-2 border-primary/40 pl-2.5 my-1.5 text-foreground/85 italic">{children}</blockquote>
);
const MdCode = ({ inline, children }) =>
  inline ? (
    <code className="mono text-[11.5px] px-1 py-[1px] rounded bg-foreground/10">{children}</code>
  ) : (
    <pre className="mono text-[11.5px] rounded-md bg-foreground/10 p-2 my-1.5 overflow-x-auto">
      <code>{children}</code>
    </pre>
  );
const MdTable = ({ children }) => (
  <div className="my-2 overflow-x-auto rounded border border-border">
    <table className="w-full text-[12px] border-collapse">{children}</table>
  </div>
);
const MdThead = ({ children }) => <thead className="bg-secondary/60">{children}</thead>;
const MdTh = ({ children }) => <th className="px-2 py-1 text-left font-semibold border-b border-border">{children}</th>;
const MdTd = ({ children }) => <td className="px-2 py-1 border-b border-border/60 align-top">{renderTextWithCitationChips(children)}</td>;
const MdA = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2 hover:opacity-80">
    {children}
  </a>
);

const ADA_MD_COMPONENTS = {
  h1: MdH, h2: MdH, h3: MdH3, h4: MdH3,
  p: MdP, strong: MdStrong, em: MdEm,
  ul: MdUl, ol: MdOl, li: MdLi,
  hr: MdHr, blockquote: MdBlockquote,
  code: MdCode,
  table: MdTable, thead: MdThead, th: MdTh, td: MdTd,
  a: MdA,
};


function VerificationBadge({ v }) {
  // Three verdicts from the backend verifier:
  //   "verified"    → every claim grounded    (green, ShieldCheck)
  //   "partial"     → mostly grounded         (amber, ShieldAlert)
  //   "unsupported" → material claims failed  (red,   AlertTriangle)
  //
  // The badge is intentionally understated — a small pill under the
  // answer with a hover tooltip listing the flagged issues. Never
  // shouts, but earns trust when a partner asks "is this right?".
  const map = {
    verified: {
      Icon: CheckCircle2,
      label: "Verified",
      cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
      dot: "bg-emerald-500",
    },
    partial: {
      Icon: ShieldAlert,
      label: "Partially grounded",
      cls: "bg-amber-500/10 text-amber-600 border-amber-500/25",
      dot: "bg-amber-500",
    },
    unsupported: {
      Icon: AlertTriangle,
      label: "Review — some claims unsupported",
      cls: "bg-rose-500/10 text-rose-600 border-rose-500/25",
      dot: "bg-rose-500",
    },
  };
  const cfg = map[v.verdict];
  if (!cfg) return null;
  const { Icon, label, cls, dot } = cfg;
  const conf = typeof v.confidence === "number" ? Math.round(v.confidence * 100) : null;
  const issues = Array.isArray(v.issues) ? v.issues.filter(Boolean) : [];
  const hasIssues = issues.length > 0;
  return (
    <div className="mt-2" data-testid={`ada-verification-${v.verdict}`}>
      <div
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${cls} text-[10.5px] mono uppercase tracking-wider`}
        title={hasIssues ? issues.join(" • ") : label}
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
        <Icon className="w-3 h-3" />
        <span>{label}</span>
        {conf !== null && <span className="opacity-70">· {conf}%</span>}
      </div>
      {hasIssues && (
        <ul className="mt-1 pl-3 text-[11px] text-muted-foreground list-disc marker:text-muted-foreground/60 space-y-0.5">
          {issues.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}


function Bubble({ msg, roomId, live, onFollowup, onClose }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
      {isUser ? (
        <div className="w-6 h-6 rounded-full bg-secondary text-foreground flex items-center justify-center shrink-0 mt-0.5">
          <span className="text-[10px] font-medium">You</span>
        </div>
      ) : (
        <div className="w-6 h-6 rounded-full overflow-hidden ring-1 ring-primary/30 shrink-0 mt-0.5">
          <img src="/ada/ada-avatar.png" alt="Ada" className="w-full h-full object-cover" draggable={false} />
        </div>
      )}
      <div className={`min-w-0 max-w-[85%] ${isUser ? "text-right" : ""}`}>
        <div className={`inline-block text-left px-3 py-2 rounded-2xl text-sm break-words ${
          isUser
            ? "bg-primary text-primary-foreground rounded-tr-sm whitespace-pre-wrap"
            : "bg-secondary text-foreground rounded-tl-sm"
        }`}>
          {isUser ? (
            <>{msg.content}</>
          ) : (
            <AdaMarkdown text={msg.content} />
          )}
          {live && <span className="inline-block w-1 h-3 ml-0.5 bg-current animate-pulse align-baseline" />}
        </div>
        {!isUser && msg.citations && msg.citations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {msg.citations.map((c, i) => {
              const inner = (
                <>
                  <FileText className="w-2.5 h-2.5" />
                  <span className="max-w-[130px] truncate">{c.doc_name}</span>
                  <span className="opacity-70">·p{c.page || 1}</span>
                </>
              );
              const cls = "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition mono text-[9.5px] uppercase tracking-wider";
              return c.doc_id ? (
                <Link
                  key={i}
                  to={`/app/rooms/${roomId}/view/${c.doc_id}?page=${c.page || 1}`}
                  onClick={onClose}
                  data-testid={`ada-citation-${i}`}
                  title={`${c.doc_name} · page ${c.page || 1}`}
                  className={cls}
                >
                  {inner}
                </Link>
              ) : (
                <span
                  key={i}
                  data-testid={`ada-citation-${i}`}
                  title={`${c.doc_name} · page ${c.page || 1}`}
                  className={cls + " cursor-default opacity-70"}
                >
                  {inner}
                </span>
              );
            })}
          </div>
        )}
        {!isUser && msg.verification && msg.verification.verdict && (
          <VerificationBadge v={msg.verification} />
        )}
        {!isUser && msg.followups && msg.followups.length > 0 && (
          <div className="mt-2.5">
            <div className="mono text-[9.5px] uppercase tracking-wider text-muted-foreground mb-1">
              You might also ask
            </div>
            <div className="flex flex-col gap-1 items-start">
              {msg.followups.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`ada-followup-${i}`}
                  onClick={() => onFollowup && onFollowup(q)}
                  className="group max-w-full inline-flex items-start gap-1.5 px-2.5 py-1 rounded-full border border-primary/30 text-primary bg-primary/[0.04] hover:bg-primary/10 hover:border-primary transition text-[11px] text-left"
                >
                  <ChevronRight className="w-2.5 h-2.5 mt-0.5 shrink-0 opacity-70 group-hover:opacity-100" />
                  <span className="truncate">{q}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {!isUser && !live && msg.id && (
          <FeedbackControls
            roomId={roomId}
            messageId={msg.id}
            messageText={msg.content || ""}
          />
        )}
      </div>
    </div>
  );
}


// FeedbackControls — a thumbs-up / thumbs-down pair with an optional
// comment box, rendered under every completed assistant answer. Sits
// out of the way (icon-only, muted) until the user hovers or engages.
// One vote per (message, user); the endpoint upserts so flipping /
// editing your own vote is idempotent.
function FeedbackControls({ roomId, messageId, messageText }) {
  const [verdict, setVerdict] = useState(null);   // "up" | "down" | null
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const copyResetTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/rooms/${roomId}/ada/messages/${messageId}/feedback`,
        );
        if (!alive) return;
        setVerdict(data?.verdict || null);
        setComment(data?.comment || "");
      } catch { /* not yet rated — silent */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [roomId, messageId]);

  const submit = async (nextVerdict, nextComment) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/rooms/${roomId}/ada/messages/${messageId}/feedback`, {
        verdict: nextVerdict,
        comment: nextComment ?? comment,
      });
      setVerdict(nextVerdict);
    } catch (e) {
      toast.error(formatError(e));
    } finally { setSubmitting(false); }
  };

  const onUp = () => {
    if (verdict === "up") return;   // no re-fire on repeated clicks
    submit("up", "");
    setShowComment(false);
  };
  const onDown = () => {
    if (verdict === "down") { setShowComment((s) => !s); return; }
    submit("down", comment);
    setShowComment(true);
  };
  const saveComment = () => {
    if (!verdict) return;
    submit(verdict, comment);
    setShowComment(false);
  };

  // Copy the assistant answer verbatim. Icon flips to a checkmark for
  // ~1.5 s as feedback — same pattern GitHub / Claude / ChatGPT use.
  // Falls back to a legacy execCommand path on browsers without
  // navigator.clipboard (rare, mostly non-HTTPS previews).
  const onCopy = async () => {
    const text = (messageText || "").trim();
    if (!text) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setJustCopied(true);
      clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setJustCopied(false), 1500);
    } catch (e) {
      toast.error("Couldn't copy — check your browser permissions.");
    }
  };
  useEffect(() => () => clearTimeout(copyResetTimer.current), []);

  if (!loaded) return null;

  return (
    <div className="mt-2 flex flex-col items-start gap-1.5" data-testid={`ada-feedback-${messageId}`}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onCopy}
          data-testid={`ada-copy-${messageId}`}
          aria-label={justCopied ? "Copied to clipboard" : "Copy answer"}
          title={justCopied ? "Copied" : "Copy answer"}
          className={`p-1 rounded-md transition ${
            justCopied
              ? "text-emerald-600 bg-emerald-500/10"
              : "text-muted-foreground/60 hover:text-foreground hover:bg-secondary"
          }`}
        >
          {justCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
        <span className="w-px h-3 bg-border/70 mx-0.5" aria-hidden />
        <button
          type="button"
          onClick={onUp}
          disabled={submitting}
          data-testid={`ada-feedback-up-${messageId}`}
          aria-label="Helpful"
          title={verdict === "up" ? "You marked this helpful" : "Mark as helpful"}
          className={`p-1 rounded-md transition ${
            verdict === "up"
              ? "text-emerald-600 bg-emerald-500/10"
              : "text-muted-foreground/60 hover:text-emerald-600 hover:bg-emerald-500/10"
          }`}
        >
          <ThumbsUp className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={onDown}
          disabled={submitting}
          data-testid={`ada-feedback-down-${messageId}`}
          aria-label="Not helpful"
          title={verdict === "down" ? "You marked this not helpful — click to add a note" : "Mark as not helpful"}
          className={`p-1 rounded-md transition ${
            verdict === "down"
              ? "text-red-600 bg-red-500/10"
              : "text-muted-foreground/60 hover:text-red-600 hover:bg-red-500/10"
          }`}
        >
          <ThumbsDown className="w-3 h-3" />
        </button>
        {verdict === "up" && !showComment && (
          <button
            type="button"
            onClick={() => setShowComment(true)}
            className="text-[10px] text-muted-foreground/70 hover:text-muted-foreground ml-0.5"
          >
            add a note
          </button>
        )}
      </div>
      {showComment && verdict && (
        <div className="w-full flex items-start gap-1.5">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={verdict === "down" ? "What was off? (optional)" : "Anything you liked? (optional)"}
            data-testid={`ada-feedback-comment-${messageId}`}
            rows={2}
            maxLength={2000}
            className="flex-1 text-[11px] px-2 py-1 rounded-md bg-background border border-border focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
          />
          <button
            type="button"
            onClick={saveComment}
            disabled={submitting}
            data-testid={`ada-feedback-save-${messageId}`}
            className="text-[10px] px-2 py-1 rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 shrink-0"
          >
            {submitting ? "…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Empty state / skeleton / disabled / composer
// ---------------------------------------------------------------------------

function EmptyState({ suggestions, onPick }) {
  return (
    <div className="py-4">
      <div className="text-center mb-4">
        <div className="w-11 h-11 mx-auto rounded-full overflow-hidden ring-1 ring-primary/30 mb-2 ada-avatar-breathe">
          <img src="/ada/ada-avatar.png" alt="Ada" className="w-full h-full object-cover" draggable={false} />
        </div>
        <h3 className="font-display text-base tracking-tight">Ada</h3>
        <p className="text-[11px] text-muted-foreground mt-1 max-w-[280px] mx-auto leading-relaxed">
          Your diligence copilot. Grounded in this room, cites every source,
          refuses to speculate.
        </p>
      </div>
      <div className="space-y-1.5">
        {suggestions.map((s, i) => (
          <button
            key={i}
            data-testid={`ada-suggestion-${i}`}
            onClick={() => onPick(s)}
            className="group w-full flex items-start gap-1.5 px-2.5 py-1.5 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition text-[12px] text-left"
          >
            <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-muted-foreground group-hover:text-primary" />
            <span className="text-foreground/90">{s}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
    </div>
  );
}

function DisabledEmpty({ canEdit, savingCfg, onToggle }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
      <ShieldOff className="w-6 h-6 text-muted-foreground mb-3" />
      <h3 className="font-display text-base">Ada is disabled here</h3>
      <p className="text-xs text-muted-foreground mt-2 max-w-[260px]">
        The App Owner has turned off Ada for this room.
      </p>
      {canEdit && (
        <button
          data-testid="ada-toggle-enable"
          onClick={onToggle}
          disabled={savingCfg}
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition text-sm disabled:opacity-50"
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Enable Ada
        </button>
      )}
    </div>
  );
}

function Composer({ input, setInput, sending, onSend, onStop }) {
  const taRef = useRef(null);
  // Auto-grow: recompute the textarea height on every input change so
  // the surface expands with the user's typing up to a comfortable
  // 3-4 line ceiling, then scrolls internally.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "0px";                    // collapse first so scrollHeight is accurate
    const next = Math.min(el.scrollHeight, 180); // cap at ~7 lines
    el.style.height = `${Math.max(28, next)}px`;
  }, [input]);

  return (
    <div className="shrink-0 border-t border-border p-3 bg-card">
      <div className="relative flex items-end gap-2 rounded-2xl bg-background border border-border focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20 transition-all pl-4 pr-2 py-1.5">
        <textarea
          ref={taRef}
          data-testid="ada-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
          }}
          disabled={sending}
          rows={1}
          placeholder="Ask Ada…"
          className="flex-1 bg-transparent border-0 outline-none text-sm resize-none disabled:opacity-60 placeholder:text-muted-foreground/60 leading-relaxed py-1 ada-scroll-slim"
          style={{ minHeight: 28, maxHeight: 180, overflowY: "auto" }}
        />
        {sending ? (
          <button
            data-testid="ada-stop-btn"
            onClick={onStop}
            className="w-8 h-8 shrink-0 rounded-full border border-border hover:bg-secondary transition inline-flex items-center justify-center self-end mb-0.5"
            title="Stop"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            data-testid="ada-send-btn"
            onClick={onSend}
            disabled={!input.trim()}
            className="w-8 h-8 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center self-end mb-0.5"
            title="Send"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="mt-1.5 mono text-[9px] text-muted-foreground/70 text-center">
        Enter to send · Shift+Enter for newline
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Chat history sub-view
// ---------------------------------------------------------------------------

function HistoryView({ convs, activeId, onOpen, onDelete, onNewChat }) {
  return (
    <div className="flex-1 overflow-y-auto">
      <button
        onClick={onNewChat}
        className="w-full px-4 py-3 text-sm text-primary hover:bg-primary/5 border-b border-border transition text-left flex items-center gap-2"
      >
        <Plus className="w-3.5 h-3.5" /> Start a new chat
      </button>
      {convs.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          <MessageSquare className="w-4 h-4 mx-auto mb-2 opacity-60" />
          No prior chats yet.
        </div>
      ) : (
        convs.map((c) => (
          <div
            key={c.id}
            className={`group px-4 py-3 border-b border-border last:border-b-0 flex items-start gap-2 cursor-pointer transition ${
              activeId === c.id ? "bg-primary/[0.06]" : "hover:bg-secondary/40"
            }`}
            onClick={() => onOpen(c.id)}
            data-testid={`ada-conv-${c.id}`}
          >
            <MessageSquare className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${activeId === c.id ? "text-primary" : "text-muted-foreground"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{c.title || "New chat"}</div>
              <div className="mono text-[10px] text-muted-foreground mt-0.5">
                {c.message_count || 0} msg{c.message_count === 1 ? "" : "s"}
                {c.updated_at ? ` · ${new Date(c.updated_at).toLocaleDateString()}` : ""}
              </div>
            </div>
            <button
              data-testid={`ada-conv-delete-${c.id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(c); }}
              title="Delete"
              className="p-1 rounded opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Access sub-view — "What Ada can see for me"
// ---------------------------------------------------------------------------

function AccessView({ access, status }) {
  if (!access) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Loader2 className="w-4 h-4 mr-2 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading access map…</span>
      </div>
    );
  }
  const { visible = [], hidden = [], is_admin, visible_count, hidden_count } = access;
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Explainer strip */}
      <div className="px-4 py-3 border-b border-border bg-secondary/40">
        <p className="text-[12px] text-foreground leading-relaxed">
          {is_admin ? (
            <>You&apos;re an <strong>Admin</strong> — Ada can read every document in this data room.</>
          ) : (
            <>
              Ada is scoped to your permissions. When she refuses a question, it&apos;s
              either because the topic isn&apos;t in the room, or the source lives in one
              of the restricted areas below.
            </>
          )}
        </p>
        {status && (
          <div className="mt-2 flex items-center gap-3 mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {visible_count} accessible
            </span>
            {!is_admin && hidden_count > 0 && (
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                {hidden_count} hidden
              </span>
            )}
            <span className="ml-auto">{status.chunks} chunks</span>
          </div>
        )}
      </div>

      {/* Restricted-scope notice — regular users see an aggregate note
          only. We deliberately do NOT enumerate restricted folder / file
          names to avoid leaking confidential structure. Admins get the
          full itemised list further below. */}
      {!is_admin && hidden_count > 0 && (
        <div className="px-4 pt-4 pb-2" data-testid="ada-access-restricted-notice">
          <div className="rounded-md bg-amber-500/5 border border-amber-500/20 px-3 py-2.5">
            <div className="mono text-[10px] uppercase tracking-wider text-amber-600 inline-flex items-center gap-1.5">
              <EyeOff className="w-3 h-3" /> Restricted scope
            </div>
            <p className="text-[12px] text-foreground/90 mt-1.5 leading-relaxed">
              There {hidden_count === 1 ? "is" : "are"} <strong>{hidden_count}</strong> document{hidden_count === 1 ? "" : "s"} in
              this room outside your permission scope. Ada won&apos;t search or cite
              them for you.
            </p>
            <p className="text-[11px] text-muted-foreground mt-1.5 inline-flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>If a topic you need seems missing, ask an Admin to grant access.</span>
            </p>
          </div>
        </div>
      )}

      {/* Admins get the itemised list so they can audit their own permission tree. */}
      {is_admin && hidden.length > 0 && (
        <div>
          <div className="px-4 pt-4 pb-1 mono text-[10px] uppercase tracking-wider text-amber-600 inline-flex items-center gap-1.5">
            <EyeOff className="w-3 h-3" /> Hidden from Ada
          </div>
          <ul className="px-2 pb-2">
            {hidden.map((h) => (
              <li key={h.id} className="px-2 py-1.5 rounded flex items-start gap-2 text-[12.5px]"
                  title={h.reason === "folder_restricted"
                    ? "Every file inside this folder is out of Ada's reach for you."
                    : "This specific file is restricted for you."}
              >
                {h.type === "folder" ? (
                  <FolderLock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
                ) : (
                  <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    {h.path ? <span className="text-muted-foreground">{h.path} / </span> : null}
                    <span>{h.name}</span>
                  </div>
                  <div className="mono text-[10px] text-muted-foreground mt-0.5">
                    {h.type === "folder"
                      ? `${h.child_count} file${h.child_count === 1 ? "" : "s"} · restricted folder`
                      : "restricted"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Visible section */}
      <div className="px-4 pt-3 pb-1 mono text-[10px] uppercase tracking-wider text-emerald-600 inline-flex items-center gap-1.5">
        <Eye className="w-3 h-3" /> Ada can read
      </div>
      <ul className="px-2 pb-4">
        {visible.length === 0 ? (
          <li className="px-2 py-3 text-xs text-muted-foreground">Nothing accessible yet.</li>
        ) : (
          visible.map((v) => (
            <li key={v.id} className="px-2 py-1.5 rounded flex items-start gap-2 text-[12.5px]">
              <FileText className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${v.indexed ? "text-emerald-600" : "text-muted-foreground"}`} />
              <div className="min-w-0 flex-1">
                <div className="truncate">
                  {v.path ? <span className="text-muted-foreground">{v.path} / </span> : null}
                  <span>{v.name}</span>
                </div>
                <div className="mono text-[10px] text-muted-foreground mt-0.5">
                  {v.indexed ? "indexed" : "pending index"}
                </div>
              </div>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Settings sub-view (Owner/Admin)
// ---------------------------------------------------------------------------

function SettingsView({ cfg, status, isAdmin, savingCfg, reindexing, onToggle, onReindex }) {
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {cfg?.can_edit && (
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium">Ada is {cfg.enabled ? "on" : "off"} for this room</div>
              <div className="mono text-[10px] text-muted-foreground mt-0.5">
                Owner-only toggle · disables the bubble for everyone
              </div>
            </div>
            <button
              data-testid="ada-toggle-disable"
              onClick={onToggle}
              disabled={savingCfg}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition ${
                cfg.enabled
                  ? "bg-secondary hover:bg-rose-500/10 hover:text-rose-600"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              } disabled:opacity-50`}
            >
              {cfg.enabled ? <ShieldOff className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
              {cfg.enabled ? "Disable" : "Enable"}
            </button>
          </div>
        </div>
      )}
      {isAdmin && (
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium">Re-index</div>
              <div className="mono text-[10px] text-muted-foreground mt-0.5">
                Re-embed any new / changed documents
              </div>
            </div>
            <button
              data-testid="ada-reindex-btn"
              onClick={onReindex}
              disabled={reindexing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-secondary transition disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${reindexing ? "animate-spin" : ""}`} />
              {reindexing ? "Working…" : "Reindex"}
            </button>
          </div>
          {status && (
            <div className="mt-2 mono text-[10px] text-muted-foreground">
              Coverage · {status.coverage_pct}% ({status.indexed_docs}/{status.total_docs}) · {status.chunks} chunks
            </div>
          )}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground text-center pt-2">
        Ada · powered by an LLM · answers grounded in this room only
      </p>
    </div>
  );
}
